import type { FloorplanZone } from '../types';

export const MIN_FLOORPLAN_ZONE_SIZE = 2;

export function isUsableFloorplanZone(zone: Pick<FloorplanZone, 'width' | 'height'>): boolean {
  return Number.isFinite(zone.width)
    && Number.isFinite(zone.height)
    && zone.width >= MIN_FLOORPLAN_ZONE_SIZE
    && zone.height >= MIN_FLOORPLAN_ZONE_SIZE;
}

export function inferZoneTypeFromLabel(label: string): FloorplanZone['type'] {
  const normalized = label.toLowerCase();
  if (normalized.includes('entrance') || normalized.includes('foyer')) return 'entrance';
  if (normalized.includes('exit')) return 'exit';
  if (normalized.includes('checkout') || normalized.includes('till')) return 'checkout';
  if (normalized.includes('service')) return 'customer_service';
  if (normalized.includes('chill') || normalized.includes('fridge')) return 'chill';
  if (normalized.includes('frozen') || normalized.includes('freezer')) return 'frozen';
  if (normalized.includes('promo') || normalized.includes('end')) return 'promotional';
  return 'grocery';
}

export function zoneFillColor(type: FloorplanZone['type']): string {
  switch (type) {
    case 'chill':
      return '#dbeafe';
    case 'frozen':
      return '#e0f2fe';
    case 'promotional':
      return '#fef3c7';
    case 'checkout':
      return '#fce7f3';
    case 'entrance':
      return '#dcfce7';
    case 'exit':
      return '#fee2e2';
    case 'customer_service':
      return '#f3e8ff';
    default:
      return '#f1f5f9';
  }
}

export function createZoneFromInstoreLocation(
  location: { id: number; name: string },
  index: number,
  existing: FloorplanZone[],
): FloorplanZone {
  const col = index % 3;
  const row = Math.floor(index / 3);
  return {
    id: `zone-loc-${location.id}`,
    instoreLocationId: location.id,
    label: location.name,
    type: inferZoneTypeFromLabel(location.name),
    x: 4 + col * 30,
    y: 4 + row * 22,
    width: 24,
    height: 16,
  };
}

/**
 * Re-link an existing floorplan shape to a different canonical retailer
 * location while preserving the shape's position and visual styling.
 */
export function reassignZoneToInstoreLocation(
  zone: FloorplanZone,
  location: { id: number; name: string },
): FloorplanZone {
  return {
    ...zone,
    instoreLocationId: location.id,
    label: location.name,
    type: inferZoneTypeFromLabel(location.name),
  };
}

export function buildZoneByInstoreLocationId(zones: FloorplanZone[]): Map<number, FloorplanZone> {
  const map = new Map<number, FloorplanZone>();
  for (const zone of zones) {
    if (zone.instoreLocationId != null && isUsableFloorplanZone(zone)) {
      map.set(zone.instoreLocationId, zone);
    }
  }
  return map;
}

export interface InstoreLocationFloorplanRow {
  location: { id: number; name: string };
  zone: FloorplanZone | null;
  isMapped: boolean;
}

/** Retailer catalog locations with their floorplan shape association status. */
export function buildInstoreLocationFloorplanRows(
  retailerLocations: Array<{ id: number; name: string }>,
  zones: FloorplanZone[],
): InstoreLocationFloorplanRow[] {
  const zoneByLocationId = buildZoneByInstoreLocationId(zones);
  return [...retailerLocations]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((location) => {
      const zone = zoneByLocationId.get(location.id) ?? null;
      return {
        location,
        zone,
        isMapped: zone != null,
      };
    });
}

export function unlinkedFloorplanZones(zones: FloorplanZone[]): FloorplanZone[] {
  return zones.filter((zone) => zone.instoreLocationId == null);
}

export function mergeZonesWithInstoreLocations(
  savedZones: FloorplanZone[],
  retailerLocations: Array<{ id: number; name: string }>,
  options: { allowAutoScaffold?: boolean } = {},
): FloorplanZone[] {
  if (savedZones.length > 0) {
    const locationById = new Map(retailerLocations.map((location) => [location.id, location]));
    return savedZones.filter(isUsableFloorplanZone).map((zone) => {
      if (zone.instoreLocationId == null) return zone;
      const location = locationById.get(zone.instoreLocationId);
      if (!location) return zone;
      // The retailer catalog is authoritative. A floorplan zone is the spatial
      // representation of that location, not a separately named section.
      return {
        ...zone,
        label: location.name,
        type: inferZoneTypeFromLabel(location.name),
      };
    });
  }
  // Never invent boxes on top of an uploaded plan — that flooded save payloads
  // and made "edit plan" look broken when validation rejected the scaffold.
  if (options.allowAutoScaffold === false) {
    return [];
  }
  return retailerLocations.map((location, index) => createZoneFromInstoreLocation(location, index, []));
}

const ZONE_TYPES = new Set<FloorplanZone['type']>([
  'entrance',
  'exit',
  'checkout',
  'customer_service',
  'chill',
  'frozen',
  'promotional',
  'grocery',
]);

function clampPct(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/** Normalize aisle/zone payloads before PUT so Zod validation cannot 400 on soft defects. */
export function sanitizeFloorplanWritePayload(input: {
  aisles: Array<Record<string, unknown>>;
  zones: Array<Record<string, unknown>>;
}): {
  aisles: Array<{
    id: string;
    number: number;
    name: string;
    category: string;
    lengthMetres: number;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  zones: Array<{
    id: string;
    label: string;
    instoreLocationId?: number | null;
    type: FloorplanZone['type'];
    x: number;
    y: number;
    width: number;
    height: number;
    outlineShape?: FloorplanZone['outlineShape'];
    outlineStrokeWidth?: number;
    outlineColor?: string;
  }>;
} {
  const aisles = input.aisles.map((aisle, index) => {
    const id = typeof aisle.id === 'string' && aisle.id.trim() ? aisle.id : `aisle-${index + 1}`;
    const number = typeof aisle.number === 'number' && Number.isFinite(aisle.number)
      ? Math.max(0, Math.trunc(aisle.number))
      : index + 1;
    const name = typeof aisle.name === 'string' && aisle.name.trim() ? aisle.name.trim() : `Aisle ${number}`;
    const category = typeof aisle.category === 'string' && aisle.category.trim()
      ? aisle.category.trim()
      : 'General';
    return {
      id,
      number,
      name,
      category,
      lengthMetres: clampPct(typeof aisle.lengthMetres === 'number' ? aisle.lengthMetres : 0, 0, 10_000),
      x: clampPct(typeof aisle.x === 'number' ? aisle.x : 0, 0, 100),
      y: clampPct(typeof aisle.y === 'number' ? aisle.y : 0, 0, 100),
      width: clampPct(typeof aisle.width === 'number' ? aisle.width : 6, 0.5, 100),
      height: clampPct(typeof aisle.height === 'number' ? aisle.height : 6, 0.5, 100),
    };
  });

  const zones = input.zones.map((zone, index) => {
    const id = typeof zone.id === 'string' && zone.id.trim() ? zone.id : `zone-${index + 1}`;
    const label = typeof zone.label === 'string' && zone.label.trim() ? zone.label.trim() : `Zone ${index + 1}`;
    const type = typeof zone.type === 'string' && ZONE_TYPES.has(zone.type as FloorplanZone['type'])
      ? (zone.type as FloorplanZone['type'])
      : inferZoneTypeFromLabel(label);
    const outlineShape: FloorplanZone['outlineShape'] = zone.outlineShape === 'rectangle'
      || zone.outlineShape === 'rounded'
      || zone.outlineShape === 'ellipse'
      ? zone.outlineShape
      : undefined;
    const outlineStrokeWidth = typeof zone.outlineStrokeWidth === 'number' && Number.isFinite(zone.outlineStrokeWidth)
      ? clampPct(zone.outlineStrokeWidth, 0.05, 2)
      : undefined;
    const outlineColor = typeof zone.outlineColor === 'string'
      && /^#[0-9a-f]{6}$/i.test(zone.outlineColor)
      ? zone.outlineColor.toLowerCase()
      : undefined;
    const instoreLocationId = typeof zone.instoreLocationId === 'number' && zone.instoreLocationId > 0
      ? Math.trunc(zone.instoreLocationId)
      : zone.instoreLocationId === null
        ? null
        : undefined;

    return {
      id,
      label,
      instoreLocationId,
      type,
      x: clampPct(typeof zone.x === 'number' ? zone.x : 0, 0, 100),
      y: clampPct(typeof zone.y === 'number' ? zone.y : 0, 0, 100),
      width: clampPct(typeof zone.width === 'number' ? zone.width : 2, MIN_FLOORPLAN_ZONE_SIZE, 100),
      height: clampPct(typeof zone.height === 'number' ? zone.height : 2, MIN_FLOORPLAN_ZONE_SIZE, 100),
      ...(outlineShape ? { outlineShape } : {}),
      ...(outlineStrokeWidth != null ? { outlineStrokeWidth } : {}),
      ...(outlineColor ? { outlineColor } : {}),
    };
  });

  return { aisles, zones };
}

export function zoneLabelById(zones: FloorplanZone[], zoneId: string | undefined): string | null {
  if (!zoneId) return null;
  return zones.find((zone) => zone.id === zoneId)?.label ?? null;
}

export function imagesForZone<T extends { id: string }>(
  images: T[],
  zoneId: string,
  tags: Record<string, string>,
): T[] {
  return images.filter((image) => tags[image.id] === zoneId);
}

export function untaggedImages<T extends { id: string }>(
  images: T[],
  tags: Record<string, string>,
): T[] {
  return images.filter((image) => !tags[image.id]);
}
